import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

const INVITE_STATUS_CACHE_KEY = 'cc_invite_status_cache_v1';
const INVITE_STATUS_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

// A usable provider id is a real LinkedIn member URN (e.g. "ACoAAB…").
// Contact-source placeholders ('ContactOut', 'Hunter', 'Prospeo', 'Cached')
// are shared by MANY contacts, so keying invite status on one would flip
// every contact from that source to "Invite sent" at once. Allowlist the URN
// shape instead of blocklisting placeholder strings one by one.
export const isUsableProviderId = (pid) => /^AC[A-Za-z0-9_-]{10,}$/.test(String(pid || ''));

const loadInviteStatusCache = () => {
    try {
        const raw = localStorage.getItem(INVITE_STATUS_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        const savedAt = Number(parsed.savedAt || 0);
        if (!savedAt || Date.now() - savedAt > INVITE_STATUS_CACHE_TTL_MS) return null;
        const byProviderId = parsed.byProviderId && typeof parsed.byProviderId === 'object' ? parsed.byProviderId : {};
        const byContactId = parsed.byContactId && typeof parsed.byContactId === 'object' ? parsed.byContactId : {};
        const byPublicId = parsed.byPublicId && typeof parsed.byPublicId === 'object' ? parsed.byPublicId : {};
        // Drop placeholder keys persisted by older builds.
        Object.keys(byProviderId).forEach(k => { if (!isUsableProviderId(k)) delete byProviderId[k]; });
        return { byProviderId, byContactId, byPublicId };
    } catch (_) {
        return null;
    }
};

const saveInviteStatusCache = (byProviderId, byContactId, byPublicId) => {
    try {
        localStorage.setItem(
            INVITE_STATUS_CACHE_KEY,
            JSON.stringify({
                v: 1,
                savedAt: Date.now(),
                byProviderId: byProviderId || {},
                byContactId: byContactId || {},
                byPublicId: byPublicId || {},
            })
        );
    } catch (_) {
        // ignore quota / private mode errors
    }
};

export const linkedInLogin = createAsyncThunk("/linkedInLogin", async (_, thunkAPI) => {
    try {
        const currentToken = thunkAPI.getState().auth.token || localStorage.getItem("token");
        if (!currentToken) {
            return thunkAPI.rejectWithValue("No authentication token found for LinkedIn login.");
        }

        // userId is extracted from auth-token by backend fetchUser middleware
        const response = await api.post("/api/linkedinauth/linkedin",
            {}, // Empty body - userId comes from auth-token
            {
                headers: {
                    "auth-token": currentToken
                }
            }
        )
        const payload = response.data || {};
        const normalizedAuthUrl = payload.authUrl || payload.url || payload?.data?.authUrl || payload?.data?.url || null;

        return {
            ...payload,
            authUrl: normalizedAuthUrl
        };
    } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to authenticate';
        return thunkAPI.rejectWithValue(message);
    }
});

export const checkLinkedInConnectionStatus = createAsyncThunk("/checkLinkedInConnectionStatus", async (_, thunkAPI) => {
    try {
        const currentToken = thunkAPI.getState().auth.token || localStorage.getItem("token");
        if (!currentToken) {
            return thunkAPI.rejectWithValue("No authentication token found to check LinkedIn status.");
        }

        const response = await api.get("/api/linkedinauth/status", {
            headers: {
                "auth-token": currentToken
            }
        });
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to check LinkedIn connection status';
        return thunkAPI.rejectWithValue(message);
    }
});

export const sendConnectionRequest = createAsyncThunk("/sendConnectionRequest", async ({ profileUrl, accountId, contactId, providerId }, thunkAPI) => {
    try {
        const currentToken = thunkAPI.getState().auth.token || localStorage.getItem("token");
        if (!currentToken) {
            return thunkAPI.rejectWithValue("No authentication token found to send connection request.");
        }

        const response = await api.post("/api/linkedinauth/invite-from-url",
            { profileUrl, accountId, providerId },
            {
                headers: {
                    "auth-token": currentToken,
                }
            }
        );

        // Return the data along with contactId and providerId for state management
        return {
            ...response.data,
            contactId,
            providerId: providerId || response.data.providerId
        };
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to send connection request';
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const checkInviteStatus = createAsyncThunk("/checkInviteStatus", async ({ accountId, providerId }, thunkAPI) => {
    try {
        const currentToken = thunkAPI.getState().auth.token || localStorage.getItem("token");

        const response = await api.post("/api/linkedinauth/invite-status",
            { accountId, providerId },
            {
                headers: {
                    "auth-token": currentToken
                }
            }
        )

        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to check status';
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

export const checkInviteStatusesBatch = createAsyncThunk("/checkInviteStatusesBatch", async ({ accountId, providerIds }, thunkAPI) => {
    try {
        const currentToken = thunkAPI.getState().auth.token || localStorage.getItem("token");

        const response = await api.post(
            "/api/linkedinauth/invite-status-batch",
            { accountId, providerIds },
            {
                headers: {
                    "auth-token": currentToken
                }
            }
        );

        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to check statuses';
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

// Resolve invite status for contacts that lack a Unipile provider id, using
// their LinkedIn profile URL. Reflects invites sent directly on LinkedIn.
export const checkInviteStatusesByUrls = createAsyncThunk("/checkInviteStatusesByUrls", async ({ accountId, contacts }, thunkAPI) => {
    try {
        const currentToken = thunkAPI.getState().auth.token || localStorage.getItem("token");
        const response = await api.post(
            "/api/linkedinauth/invite-status-by-urls",
            { accountId, contacts },
            { headers: { "auth-token": currentToken } }
        );
        return response.data; // { statuses: { [contactId]: status }, providerIds: {...} }
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to check statuses';
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

// Load all persisted invite statuses from our DB (survives logout). Keyed by
// provider id and by LinkedIn public identifier.
export const fetchMyInviteStatuses = createAsyncThunk("/fetchMyInviteStatuses", async (_, thunkAPI) => {
    try {
        const currentToken = thunkAPI.getState().auth.token || localStorage.getItem("token");
        const response = await api.get("/api/linkedinauth/my-invite-statuses", {
            headers: { "auth-token": currentToken }
        });
        return response.data; // { byProviderId, byPublicId }
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load invite statuses';
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

// Paginated list of the user's PLATFORM contacts who are LinkedIn connections.
// Checked per-contact by profile URL on the backend (no bulk relations fetch).
export const fetchPlatformConnections = createAsyncThunk("/fetchPlatformConnections", async ({ accountId, skip = 0, limit = 10 } = {}, thunkAPI) => {
    try {
        const currentToken = thunkAPI.getState().auth.token || localStorage.getItem("token");
        const response = await api.get(`/api/linkedinauth/platform-connections?accountId=${encodeURIComponent(accountId)}&skip=${skip}&limit=${limit}`, {
            headers: { "auth-token": currentToken }
        });
        return { ...response.data, skip }; // { contacts, total, nextSkip, hasMore, skip }
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load platform connections';
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const fetchAllConnections = createAsyncThunk("/fetchAllConnections", async ({ accountId }, thunkAPI) => {
    try {
        const response = await api.post("/api/linkedinauth/fetchconnections",
            { accountId }
        )

        return response.data.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch connections';
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

export const sendFirstLiMessage = createAsyncThunk("/sendFirstLiMessage", async ({ accountId, attendeeId, text, userId }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const response = await api.post("/api/linkedinauth/start-chat",
            { accountId, attendeeId, text, userId },
            {
                headers: {
                    "auth-token": token
                }
            }
        )

        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to send message';
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

export const getLatestPosts = createAsyncThunk("/getLatestPosts", async ({ providerId, accountId, limit }, thunkAPI) => {
    try {
        const response = await api.post(`/api/linkedinauth/${providerId}/posts`,
            { accountId, limit }
        )

        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to get posts';
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

export const sendComment = createAsyncThunk("/sendComment", async ({ accountId, text, socialId }, thunkAPI) => {
    try {
        const response = await api.post("/api/linkedinauth/comments",
            { accountId, text, socialId }
        )

        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to post comment';
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

const liAuthSlice = createSlice({
    name: "linkedinAuth",
    initialState: {
        user: JSON.parse(localStorage.getItem("user")) || null,
        token: localStorage.getItem("token") || null,
        linkedInAuthUrl: null,
        isLinkedInConnected: [],
        connectionChecked: false, // true once checkLinkedInConnectionStatus has resolved at least once
        isLoading: false,
        error: null,
        invitation: null,
        inviteLoadingByContact: {},
        ...(() => {
            const c = loadInviteStatusCache();
            return {
                inviteStatusesByProviderId: c?.byProviderId || {},
                inviteStatusesByContactId: c?.byContactId || {},
                inviteStatusesByPublicId: c?.byPublicId || {},
            };
        })(),
        inviteError: null,
        firstMessageLoading: false,
        firstMessageError: null,
        recentPosts: [],
        recentPostsLoading: false,
        recentPostsError: null,
        sendCommentStatus: null,
        sendCommentLoading: false,
        sendCommentError: null,
        connections: [],
        connectionsLoading: false,
        connectionsError: null,
        // Paginated platform connections (Inbox)
        platformConnections: [],
        platformConnectionsLoading: false,
        platformConnectionsHasMore: false,
        platformConnectionsNextSkip: 0,
        platformConnectionsError: null,
    },
    reducers: {
        clearInviteStatus: (state) => {
            state.invitation = null;
            state.inviteError = null;
        },
        // Cleared right after the connect popup is opened so it can't reopen on a
        // remount, and so only one component owns the popup.
        clearLinkedInAuthUrl: (state) => {
            state.linkedInAuthUrl = null;
        },
        clearContactInviteLoading: (state, action) => {
            const contactId = action.payload;
            if (contactId && state.inviteLoadingByContact[contactId]) {
                delete state.inviteLoadingByContact[contactId];
            }
        },
        setContactInviteStatus: (state, action) => {
            const { providerId, contactId, status } = action.payload;
            if (isUsableProviderId(providerId)) {
                state.inviteStatusesByProviderId[String(providerId)] = status;
            }
            if (contactId) {
                state.inviteStatusesByContactId[String(contactId)] = status;
            }
            saveInviteStatusCache(state.inviteStatusesByProviderId, state.inviteStatusesByContactId, state.inviteStatusesByPublicId);
        }
    },
    extraReducers: (builder) => {
        builder
            // LinkedIn Login
            .addCase(linkedInLogin.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.linkedInAuthUrl = null;
            })
            .addCase(linkedInLogin.fulfilled, (state, action) => {
                state.isLoading = false;
                state.linkedInAuthUrl = action.payload.authUrl || action.payload.url || null;
            })
            .addCase(linkedInLogin.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.linkedInAuthUrl = null;
            })

            // Check connection status
            .addCase(checkLinkedInConnectionStatus.fulfilled, (state, action) => {
                state.isLinkedInConnected = action.payload;
                state.connectionChecked = true;
                state.error = null;
            })
            .addCase(checkLinkedInConnectionStatus.rejected, (state, action) => {
                state.isLinkedInConnected = false;
                state.connectionChecked = true;
                state.error = action.payload;
            })

            // Send invite through url
            .addCase(sendConnectionRequest.pending, (state, action) => {
                const rawContactId = action.meta.arg?.contactId; // Get contactId from the original argument
                const contactId = rawContactId != null ? String(rawContactId) : '';
                if (contactId) {
                    state.inviteLoadingByContact[contactId] = true;
                }
                state.inviteError = null;
                state.invitation = null;
            })
            .addCase(sendConnectionRequest.fulfilled, (state, action) => {
                const rawContactId = action.payload?.contactId;
                const contactId = rawContactId != null ? String(rawContactId) : '';
                const providerId = action.payload?.providerId;

                // Clear loading state for this contact
                if (contactId && state.inviteLoadingByContact[contactId]) {
                    delete state.inviteLoadingByContact[contactId];
                }

                // Update status to 'pending' for this contact after successful send
                if (isUsableProviderId(providerId)) {
                    state.inviteStatusesByProviderId[String(providerId)] = 'pending';
                }
                if (contactId) {
                    state.inviteStatusesByContactId[contactId] = 'pending';
                }
                saveInviteStatusCache(state.inviteStatusesByProviderId, state.inviteStatusesByContactId, state.inviteStatusesByPublicId);

                state.invitation = action.payload;
                state.inviteError = null;
            })
            .addCase(sendConnectionRequest.rejected, (state, action) => {
                const rawContactId = action.meta.arg?.contactId;
                const contactId = rawContactId != null ? String(rawContactId) : '';
                const providerId = action.meta.arg?.providerId;
                const errMsg = String(action.payload || '');

                if (contactId && state.inviteLoadingByContact[contactId]) {
                    delete state.inviteLoadingByContact[contactId];
                }

                // LinkedIn can reject with "already invited recently" — treat as pending/cooldown (not a hard failure).
                const isAlreadyInvitedRecently =
                    errMsg.toLowerCase().includes('already been sent recently') ||
                    errMsg.toLowerCase().includes('already invited recently');

                const nextStatus = isAlreadyInvitedRecently ? 'pending' : 'failed';

                if (isUsableProviderId(providerId)) {
                    state.inviteStatusesByProviderId[String(providerId)] = nextStatus;
                }
                if (contactId) {
                    state.inviteStatusesByContactId[contactId] = nextStatus;
                }
                saveInviteStatusCache(state.inviteStatusesByProviderId, state.inviteStatusesByContactId, state.inviteStatusesByPublicId);

                state.inviteError = action.payload;
                state.invitation = null;
            })

            // Check invitation status
            .addCase(checkInviteStatus.fulfilled, (state, action) => {
                const providerId = action.payload?.providerId;
                const rawContactId = action.payload?.contactId;
                const contactId = rawContactId != null ? String(rawContactId) : '';
                const status = action.payload?.status;
                if (isUsableProviderId(providerId)) {
                    state.inviteStatusesByProviderId[String(providerId)] = status;
                }
                if (contactId) {
                    state.inviteStatusesByContactId[contactId] = status;
                }
                saveInviteStatusCache(state.inviteStatusesByProviderId, state.inviteStatusesByContactId, state.inviteStatusesByPublicId);
                state.error = null;
            })
            .addCase(checkInviteStatus.rejected, (state, action) => {
                const { providerId } = action.meta.arg;
                if (isUsableProviderId(providerId)) {
                    state.inviteStatusesByProviderId[String(providerId)] = 'error';
                    saveInviteStatusCache(state.inviteStatusesByProviderId, state.inviteStatusesByContactId, state.inviteStatusesByPublicId);
                }
                state.error = action.payload;
            })

            // Persisted invite statuses loaded from our DB (survives logout)
            .addCase(fetchMyInviteStatuses.fulfilled, (state, action) => {
                const byProviderId = action.payload?.byProviderId || {};
                const byPublicId = action.payload?.byPublicId || {};
                Object.entries(byProviderId).forEach(([pid, status]) => {
                    if (isUsableProviderId(pid)) state.inviteStatusesByProviderId[String(pid)] = status;
                });
                Object.entries(byPublicId).forEach(([pubId, status]) => {
                    if (pubId) state.inviteStatusesByPublicId[String(pubId).toLowerCase()] = status;
                });
                saveInviteStatusCache(state.inviteStatusesByProviderId, state.inviteStatusesByContactId, state.inviteStatusesByPublicId);
                state.error = null;
            })

            // Batch invitation statuses
            .addCase(checkInviteStatusesBatch.fulfilled, (state, action) => {
                const statuses = action.payload?.statuses || {};
                if (statuses && typeof statuses === 'object') {
                    Object.entries(statuses).forEach(([providerId, status]) => {
                        if (isUsableProviderId(providerId)) {
                            state.inviteStatusesByProviderId[String(providerId)] = status;
                        }
                    });
                    saveInviteStatusCache(state.inviteStatusesByProviderId, state.inviteStatusesByContactId, state.inviteStatusesByPublicId);
                }
                state.error = null;
            })
            .addCase(checkInviteStatusesBatch.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Batch invitation statuses resolved by profile URL (keyed by contactId)
            .addCase(checkInviteStatusesByUrls.fulfilled, (state, action) => {
                const statuses = action.payload?.statuses || {};
                const providerIds = action.payload?.providerIds || {};
                Object.entries(statuses).forEach(([contactId, status]) => {
                    if (contactId) state.inviteStatusesByContactId[String(contactId)] = status;
                });
                Object.entries(providerIds).forEach(([contactId, pid]) => {
                    if (isUsableProviderId(pid)) state.inviteStatusesByProviderId[String(pid)] = statuses[contactId] || state.inviteStatusesByProviderId[String(pid)];
                });
                saveInviteStatusCache(state.inviteStatusesByProviderId, state.inviteStatusesByContactId, state.inviteStatusesByPublicId);
                state.error = null;
            })
            .addCase(checkInviteStatusesByUrls.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Send first linkedin message
            .addCase(sendFirstLiMessage.pending, (state) => {
                state.firstMessageLoading = true;
                state.firstMessageError = null;
            })
            .addCase(sendFirstLiMessage.fulfilled, (state) => {
                state.firstMessageLoading = false;
                state.firstMessageError = null;
            })
            .addCase(sendFirstLiMessage.rejected, (state, action) => {
                state.firstMessageLoading = false;
                state.firstMessageError = action.payload
            })

            // Get latest posts
            .addCase(getLatestPosts.pending, (state) => {
                state.recentPostsLoading = true;
                state.recentPostsError = null;
            })
            .addCase(getLatestPosts.fulfilled, (state, action) => {
                state.recentPosts = action.payload;
                state.recentPostsLoading = false;
            })
            .addCase(getLatestPosts.rejected, (state, action) => {
                state.recentPostsLoading = false;
                state.recentPostsError = action.payload
            })

            // Send comment
            .addCase(sendComment.pending, (state) => {
                state.sendCommentLoading = true;
                state.sendCommentError = null;
            })
            .addCase(sendComment.fulfilled, (state, action) => {
                state.sendCommentStatus = action.payload.success;
                state.sendCommentLoading = false;
            })
            .addCase(sendComment.rejected, (state, action) => {
                state.sendCommentLoading = false;
                state.sendCommentError = action.payload;
            })

            // Fetch connections
            .addCase(fetchAllConnections.pending, (state) => {
                state.connectionsLoading = true;
                state.connectionsError = null;
            })
            .addCase(fetchAllConnections.fulfilled, (state, action) => {
                state.connections = action.payload;
                state.connectionsLoading = false;
            })
            .addCase(fetchAllConnections.rejected, (state, action) => {
                state.connectionsLoading = false;
                state.connectionsError = action.payload;
            })

            // Paginated platform connections
            .addCase(fetchPlatformConnections.pending, (state) => {
                state.platformConnectionsLoading = true;
                state.platformConnectionsError = null;
            })
            .addCase(fetchPlatformConnections.fulfilled, (state, action) => {
                const { contacts = [], hasMore = false, nextSkip = 0, skip = 0 } = action.payload || {};
                // skip === 0 → first page (replace); else append (load more), deduped.
                if (skip === 0) {
                    state.platformConnections = contacts;
                } else {
                    const existing = new Set(state.platformConnections.map(c => String(c._id || c.providerId)));
                    state.platformConnections = [
                        ...state.platformConnections,
                        ...contacts.filter(c => !existing.has(String(c._id || c.providerId))),
                    ];
                }
                state.platformConnectionsHasMore = hasMore;
                state.platformConnectionsNextSkip = nextSkip;
                state.platformConnectionsLoading = false;
            })
            .addCase(fetchPlatformConnections.rejected, (state, action) => {
                state.platformConnectionsLoading = false;
                state.platformConnectionsError = action.payload;
            })
    }
});

export const { clearInviteStatus, clearContactInviteLoading, setContactInviteStatus, clearLinkedInAuthUrl } = liAuthSlice.actions;
export default liAuthSlice.reducer;